import { Component, OnDestroy, OnInit, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Subscription, filter, map, of, switchMap } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  dynamicFormsOperationsTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { readLocalizedText, serializeFormSchema } from '../../shared/dynamic-form/form-schema.models';
import { DynamicApiService, DynamicForm } from './dynamic-api.service';
import { AiAuthoringWizardComponent } from './ai-authoring/ai-authoring-wizard.component';
import { AiFormDraftAcceptedEvent } from './ai-authoring/ai-authoring.models';
import { hasAiGeneratePermission } from './ai-authoring/ai-authoring.util';

@Component({
  standalone: true,
  selector: 'ain-dynamic-forms',
  imports: [
    AinPageComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    FormsModule,
    RouterLink,
    ConfirmDialogModule,
    AiAuthoringWizardComponent
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <ain-page [title]="t('dynamic.forms.title')" [description]="t('dynamic.forms.description')">
      <div class="toolbar">
        @if (canGenerateWithAi()) {
          <button
            pButton
            type="button"
            severity="help"
            icon="pi pi-sparkles"
            [label]="t('dynamic.aiAuthoring.generateWithAi')"
            (click)="openAiAuthoringWizard()"></button>
        }
        <button pButton icon="pi pi-plus" [label]="t('common.create')" (click)="showCreate.set(true)"></button>
        <button pButton severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        <span class="live-status" [class]="liveUpdateService.connectionState()">
          {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
        </span>
      </div>
      <p-table [value]="forms()" [paginator]="true" [rows]="10" class="mt">
        <ng-template pTemplate="header">
          <tr><th>{{ t('columns.name') }}</th><th>{{ t('columns.status') }}</th><th></th></tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.name }}</td>
            <td>{{ row.status }}</td>
            <td>
              <a [routerLink]="['/dynamic/forms', row.id]">{{ t('common.open') }}</a>
              @if (canDeleteForm()) {
                <button
                  pButton
                  type="button"
                  text
                  severity="danger"
                  icon="pi pi-trash"
                  [attr.aria-label]="t('dynamic.form.delete.action')"
                  (click)="confirmDeleteForm(row)"></button>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    </ain-page>
    <p-dialog [(visible)]="createVisible" [header]="t('common.create')" [modal]="true">
      <input pInputText [(ngModel)]="newName" class="w-full" />
      <ng-template pTemplate="footer">
        <button pButton [label]="t('common.save')" (click)="create()"></button>
      </ng-template>
    </p-dialog>

    <ain-ai-authoring-wizard
      #formAiWizard
      mode="form"
      [(visible)]="aiWizardVisible"
      (formDraftAccepted)="onFormDraftAccepted($event)" />
    <p-confirmDialog />
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 16px; }
    .mt { margin-top: 0; }
    .w-full { width: 100%; }
    a { color: var(--ain-color-primary); }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
  `]
})
export class DynamicFormsComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly api = inject(DynamicApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  readonly forms = signal<DynamicForm[]>([]);
  readonly showCreate = signal(false);
  aiWizardVisible = false;
  newName = '';
  get createVisible(): boolean { return this.showCreate(); }
  set createVisible(v: boolean) { this.showCreate.set(v); }

  readonly formAiWizard = viewChild<AiAuthoringWizardComponent>('formAiWizard');

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.dynamicFormsOperationsChanged))
      .subscribe(() => this.load(false));

    void this.setupLiveSubscription();
    this.load();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  load(showLoading = true): void {
    if (showLoading) {
      this.api.listForms().subscribe({ next: items => this.forms.set(items) });
      return;
    }

    this.api.listForms().subscribe({ next: items => this.forms.set(items) });
  }

  create(): void {
    this.api.createForm(this.newName).subscribe({ next: () => { this.showCreate.set(false); this.load(false); } });
  }

  canDeleteForm(): boolean {
    return this.hasFormAction('Delete');
  }

  confirmDeleteForm(row: DynamicForm): void {
    this.confirmation.confirm({
      header: this.t('dynamic.form.delete.confirmTitle'),
      message: this.t('dynamic.form.delete.confirmMessage'),
      accept: () => this.deleteForm(row.id)
    });
  }

  private deleteForm(formId: string): void {
    this.api.deleteForm(formId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.form.delete.success') });
        this.load(false);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('dynamic.form.delete.failed') })
    });
  }

  private hasFormAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.forms');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  canGenerateWithAi(): boolean {
    return hasAiGeneratePermission(this.contextService.context());
  }

  openAiAuthoringWizard(): void {
    this.formAiWizard()?.open();
  }

  onFormDraftAccepted(event: AiFormDraftAcceptedEvent): void {
    const culture = this.localization.activeCulture();
    const name = readLocalizedText(event.schema.title, culture)?.trim() || 'AI Form';

    this.api.createForm(name).pipe(
      switchMap(form => {
        const schemaJson = serializeFormSchema(event.schema);
        return this.api.addFormVersion(form.id, schemaJson).pipe(
          switchMap(() => {
            if (event.draftId) {
              return this.api.markAiDraftApplied(event.draftId, { formId: form.id }).pipe(map(() => form));
            }
            return of(form);
          })
        );
      })
    ).subscribe({
      next: form => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.aiAuthoring.formPreview.loaded') });
        void this.router.navigate(['/dynamic/forms', form.id]);
      },
      error: () => {
        this.messages.add({ severity: 'error', summary: this.t('dynamic.aiAuthoring.applyFailed') });
      }
    });
  }

  t(key: string): string { return this.localization.translate(key); }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;

    this.subscribedTopic = dynamicFormsOperationsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }
}
